import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import Chip from "@/components/ui/chip";
import Popover from "@/components/ui/popover";
import ConfirmModal from "@/components/ui/confirm-modal";
import {
  Actions as ActionsIcon,
  Eye as EyeIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Pdf as PdfIcon,
} from "@/components/icons";
import Button from "@/components/ui/button";
import type { FilterField, TableColumn, SortOption } from "@/types/component";
import type {
  Intern,
  InternType,
  InternStatus,
  InternTypeV2,
  InternSubCategory,
  InternLocation,
  InternDepartmentType,
} from "../types/intern-management.types";
import {
  internManagementApi,
  triggerBrowserDownload,
} from "../services/intern-management.api";
import { translateInternApiError } from "../utils/error-mapper";
import { fetchDepartments } from "@/features/announcements/services/departments.api";
import AddInternModal from "../components/AddInternModal";
import InternDetailDrawer from "../components/InternDetailDrawer";
import type { AddInternFormData } from "../components/AddInternModal";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import styles from "./InternManagementPage.module.scss";

const buildInternTypeLabels = (t: (k: string) => string): Record<InternType, string> => ({
  universiteZorunlu: t("internManagement.addModal.internTypeOptions.university"),
  liseBeceri: t("internManagement.highSchool"),
});

const buildInternTypeV2Labels = (t: (k: string) => string): Record<InternTypeV2, string> => ({
  UNIVERSITY: t("internManagement.addModal.internTypeOptions.university"),
  HIGH_SCHOOL: t("internManagement.highSchool"),
});

const buildSubCategoryLabels = (t: (k: string) => string): Record<InternSubCategory, string> => ({
  MANDATORY_SUMMER: t("internManagement.addModal.internTypeOptions.mandatorySummer"),
  WORKPLACE_TRAINING: t("internManagement.addModal.internTypeOptions.workplaceTraining"),
  SKILL_TRAINING: t("internManagement.addModal.internTypeOptions.skillTraining"),
});

const buildLocationLabels = (t: (k: string) => string): Record<InternLocation, string> => ({
  CENTER: t("internManagement.addModal.departmentTypeOptions.central"),
  PROVINCIAL: t("internManagement.addModal.departmentTypeOptions.provincial"),
});

const buildDepartmentTypeLabels = (t: (k: string) => string): Record<InternDepartmentType, string> => ({
  PRESIDENCY: t("internManagement.addModal.subCategoryOptions.presidency"),
  BUSINESS_UNIT: t("internManagement.addModal.subCategoryOptions.businessUnit"),
});

const buildStatusConfig = (t: (k: string) => string): Record<
  InternStatus,
  { label: string; type: "success" | "info" }
> => ({
  [0]: { label: t("internManagement.addModal.statusOptions.active"), type: "success" },
  [1]: { label: t("internManagement.addModal.statusOptions.completed"), type: "info" },
});

/** İsimden Ad/Soyad böl — "Ahmet Mehmet Yılmaz" → ["Ahmet Mehmet", "Yılmaz"] (son kelime soyad) */
function splitFullName(full: string): { firstName: string; lastName: string } {
  const parts = (full ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: "—", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  const lastName = parts[parts.length - 1];
  const firstName = parts.slice(0, -1).join(" ");
  return { firstName, lastName };
}

/** Tarih ISO → DD.MM.YYYY */
function formatDateShort(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
}

const SEARCH_DEBOUNCE_MS = 400;

const InternManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const INTERN_TYPE_LABELS = useMemo(() => buildInternTypeLabels(t), [t]);
  const INTERN_TYPE_V2_LABELS = useMemo(() => buildInternTypeV2Labels(t), [t]);
  const SUB_CATEGORY_LABELS = useMemo(() => buildSubCategoryLabels(t), [t]);
  const LOCATION_LABELS = useMemo(() => buildLocationLabels(t), [t]);
  const DEPARTMENT_TYPE_LABELS = useMemo(() => buildDepartmentTypeLabels(t), [t]);
  const STATUS_CONFIG = useMemo(() => buildStatusConfig(t), [t]);
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(PERMISSIONS.INTERN_CREATE);
  const canUpdate = hasPermission(PERMISSIONS.INTERN_UPDATE);
  const canDelete = hasPermission(PERMISSIONS.INTERN_DELETE);
  const [interns, setInterns] = useState<Intern[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedRows, setSelectedRows] = useState<Intern[]>([]);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>(
    {},
  );
  const [filterValues, setFilterValues] = useState<Record<string, unknown>>({});
  const [sortBy, setSortBy] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [departmentOptions, setDepartmentOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [departmentSearchTerm, setDepartmentSearchTerm] = useState("");
  const departmentSearchDebounceRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const departmentInitialLoadDoneRef = useRef(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editInternId, setEditInternId] = useState<string | null>(null);
  const [selectedIntern, setSelectedIntern] = useState<Intern | null>(null);
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);
  const [internToDelete, setInternToDelete] = useState<Intern | null>(null);
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [certificateDownloadingId, setCertificateDownloadingId] = useState<
    string | null
  >(null);
  const certificatePollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const DEPARTMENT_PAGE_SIZE = 20;

  useEffect(() => {
    fetchDepartments({ page: 1, limit: DEPARTMENT_PAGE_SIZE })
      .then((list) =>
        setDepartmentOptions(list.map((d) => ({ label: d.name, value: d.id }))),
      )
      .catch(() => setDepartmentOptions([]))
      .finally(() => {
        departmentInitialLoadDoneRef.current = true;
      });
  }, []);

  useEffect(() => {
    if (!departmentInitialLoadDoneRef.current) return;
    if (departmentSearchDebounceRef.current)
      clearTimeout(departmentSearchDebounceRef.current);
    departmentSearchDebounceRef.current = setTimeout(() => {
      fetchDepartments({
        page: 1,
        limit: DEPARTMENT_PAGE_SIZE,
        search: departmentSearchTerm.trim() || undefined,
      })
        .then((list) =>
          setDepartmentOptions(
            list.map((d) => ({ label: d.name, value: d.id })),
          ),
        )
        .catch(() => setDepartmentOptions([]));
      departmentSearchDebounceRef.current = null;
    }, 300);
    return () => {
      if (departmentSearchDebounceRef.current)
        clearTimeout(departmentSearchDebounceRef.current);
    };
  }, [departmentSearchTerm]);

  const buildFilters = useCallback(() => {
    // Dropdown filter değerleri (multiple desteği için array olabilir)
    const pickFirst = <T,>(v: unknown): T | undefined => {
      const raw = Array.isArray(v) ? v[0] : v;
      return raw != null && raw !== "" ? (raw as T) : undefined;
    };
    const statusRaw = pickFirst<string | number>(filterValues.status);
    const statusNum =
      statusRaw != null
        ? typeof statusRaw === "number"
          ? statusRaw
          : Number(statusRaw)
        : undefined;
    return {
      search: searchTerm || undefined,
      internTypeV2: pickFirst<InternTypeV2>(filterValues.internTypeV2),
      subCategory: pickFirst<InternSubCategory>(filterValues.subCategory),
      location: pickFirst<InternLocation>(filterValues.location),
      departmentType: pickFirst<InternDepartmentType>(filterValues.departmentType),
      departmentID: pickFirst<string>(filterValues.departmentID),
      status:
        statusNum != null && !Number.isNaN(statusNum)
          ? (statusNum as InternStatus)
          : undefined,
      startDateFrom: pickFirst<string>(filterValues.startDateFrom),
      startDateTo: pickFirst<string>(filterValues.startDateTo),
    };
  }, [filterValues, searchTerm]);

  const fetchInterns = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await internManagementApi.getAll({
        page: currentPage,
        pageSize,
        sortBy,
        sortOrder,
        filters: buildFilters(),
      });
      setInterns(response.data);
      setTotalRecords(response.total);
    } catch (error) {
      console.error("Error fetching interns:", error);
      setInterns([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, sortBy, sortOrder, buildFilters]);

  useEffect(() => {
    fetchInterns();
  }, [fetchInterns]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1);
      searchDebounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchInput]);

  const handleSearch = (value: string) => {
    setSearchInput(value);
  };

  const handlePageChange = (page: number) => setCurrentPage(page);
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleRowSelect = (row: Intern) => {
    setSelectedRows((prev) => {
      const isSelected = prev.some((r) => r.id === row.id);
      if (isSelected) return prev.filter((r) => r.id !== row.id);
      return [...prev, row];
    });
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedRows(checked ? interns : []);
  };

  const handleDropdownToggle = (rowId: string, isOpen: boolean) => {
    setOpenDropdowns((prev) => ({ ...prev, [rowId]: isOpen }));
  };

  const handleAddIntern = () => {
    setIsAddModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!internToDelete) return;
    setDeleteConfirmLoading(true);
    try {
      await internManagementApi.delete(internToDelete.id);
      toast.success(
        t("internManagement.deleteSuccess"),
      );
      setInternToDelete(null);
      fetchInterns();
    } catch (error) {
      toast.error(
        translateInternApiError(
          error,
          t,
          t("internManagement.deleteError"),
        ),
      );
    } finally {
      setDeleteConfirmLoading(false);
    }
  };

  const handleAddInternSuccess = (_data: AddInternFormData) => {
    fetchInterns();
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      const { blob, filename } = await internManagementApi.exportExcel({
        filters: buildFilters(),
        sortBy,
        sortOrder,
      });
      triggerBrowserDownload(blob, filename);
      toast.success(
        t("internManagement.export.success"),
      );
    } catch (error) {
      toast.error(
        translateInternApiError(
          error,
          t,
          t("internManagement.export.error"),
        ),
      );
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadCertificate = async (intern: Intern) => {
    setCertificateDownloadingId(intern.id);
    try {
      const { blob, filename } = await internManagementApi.downloadCertificate(
        intern.id,
      );
      triggerBrowserDownload(blob, filename);
      toast.success(
        t("internManagement.certificate.downloadSuccess"),
      );
    } catch (error) {
      toast.error(
        translateInternApiError(
          error,
          t,
          t("internManagement.certificate.downloadError"),
        ),
      );
    } finally {
      setCertificateDownloadingId(null);
    }
  };

  // Tamamlanmış ancak sertifikası hazır olmayan kayıtlar için 5sn sonra refetch
  // (BullMQ arka planda ürettiğinden).
  useEffect(() => {
    const hasPending = interns.some(
      (i) => i.status === 1 && i.hasCertificate === false,
    );
    if (!hasPending) {
      if (certificatePollTimerRef.current) {
        clearTimeout(certificatePollTimerRef.current);
        certificatePollTimerRef.current = null;
      }
      return;
    }
    if (certificatePollTimerRef.current) return;
    certificatePollTimerRef.current = setTimeout(() => {
      certificatePollTimerRef.current = null;
      fetchInterns();
    }, 5000);
    return () => {
      if (certificatePollTimerRef.current) {
        clearTimeout(certificatePollTimerRef.current);
        certificatePollTimerRef.current = null;
      }
    };
  }, [interns, fetchInterns]);

  const handleFilterChange = (filters: Record<string, unknown>) => {
    setFilterValues(filters);
    setCurrentPage(1);
  };

  const sortOptions: SortOption[] = [
    {
      label: t("internManagement.sort.newest"),
      value: "createdAt-desc",
    },
    {
      label: t("internManagement.sort.oldest"),
      value: "createdAt-asc",
    },
    {
      label: t("internManagement.sort.nameAsc"),
      value: "fullName-asc",
    },
    {
      label: t("internManagement.sort.nameDesc"),
      value: "fullName-desc",
    },
  ];

  const handleSortChange = (option: SortOption) => {
    const val = String(option?.value ?? "createdAt-desc");
    const [by, order] = val.split("-");
    setSortBy(by === "fullName" ? "fullName" : "createdAt");
    setSortOrder((order === "asc" ? "asc" : "desc") as "asc" | "desc");
    setCurrentPage(1);
  };

  const filterFields: FilterField[] = [
    {
      name: "internTypeV2",
      label: t("internManagement.filters.internType"),
      type: "dropdown",
      placeholder: t("internManagement.filters.internTypePlaceholder"),
      options: Object.entries(INTERN_TYPE_V2_LABELS).map(([value, label]) => ({
        label,
        value,
      })),
    },
    {
      name: "subCategory",
      label: t("internManagement.filters.subCategory"),
      type: "dropdown",
      placeholder: t("internManagement.addModal.subCategoryPlaceholder"),
      options: Object.entries(SUB_CATEGORY_LABELS).map(([value, label]) => ({
        label,
        value,
      })),
    },
    {
      name: "location",
      label: t("internManagement.filters.location"),
      type: "dropdown",
      placeholder: t("internManagement.addModal.departmentTypePlaceholder"),
      options: Object.entries(LOCATION_LABELS).map(([value, label]) => ({
        label,
        value,
      })),
    },
    {
      name: "departmentType",
      label: t("internManagement.filters.departmentType"),
      type: "dropdown",
      placeholder: t("internManagement.addModal.subCategoryPlaceholderActive"),
      options: Object.entries(DEPARTMENT_TYPE_LABELS).map(([value, label]) => ({
        label,
        value,
      })),
    },
    {
      name: "departmentID",
      label: t("internManagement.filters.unit"),
      type: "dropdown",
      placeholder: t("internManagement.addModal.departmentSelectPlaceholder"),
      options: departmentOptions,
      searchable: true,
      onSearch: (term) => setDepartmentSearchTerm(term),
    },
    {
      name: "status",
      label: t("internManagement.filters.status"),
      type: "dropdown",
      placeholder: t("internManagement.addModal.statusPlaceholder2"),
      options: Object.entries(STATUS_CONFIG).map(([value, config]) => ({
        label: config.label,
        value,
      })),
    },
    {
      name: "startDateFrom",
      label: t("internManagement.filters.startDateFrom"),
      type: "date",
      placeholder: "YYYY-MM-DD",
    },
    {
      name: "startDateTo",
      label: t("internManagement.filters.startDateTo"),
      type: "date",
      placeholder: "YYYY-MM-DD",
    },
  ];

  const columns: TableColumn<Intern>[] = [
    {
      header: t("internManagement.columns.firstName"),
      accessor: (row) => {
        const { firstName } = row.firstName
          ? { firstName: row.firstName }
          : splitFullName(row.name);
        return firstName;
      },
      className: "intern-firstname-column",
    },
    {
      header: t("internManagement.columns.lastName"),
      accessor: (row) => {
        const { lastName } = row.lastName
          ? { lastName: row.lastName }
          : splitFullName(row.name);
        return lastName || "—";
      },
      className: "intern-lastname-column",
    },
    {
      header: t("internManagement.columns.internType"),
      accessor: (row) => {
        // Yeni spec tercih, fallback eski combined label
        if (row.internTypeV2) return INTERN_TYPE_V2_LABELS[row.internTypeV2];
        return INTERN_TYPE_LABELS[row.internType];
      },
      className: "intern-type-column",
    },
    {
      header: t("internManagement.columns.subCategory"),
      accessor: (row) =>
        row.subCategory ? SUB_CATEGORY_LABELS[row.subCategory] : "—",
      className: "intern-subcategory-column",
    },
    {
      header: t("internManagement.columns.location"),
      accessor: (row) =>
        row.location ? LOCATION_LABELS[row.location] : "—",
      className: "intern-location-column",
    },
    {
      header: t("internManagement.columns.unit"),
      accessor: "unitOrBusiness",
      className: "intern-unit-column",
    },
    {
      header: t("internManagement.columns.startDate"),
      accessor: (row) => formatDateShort(row.startDate),
      className: "intern-startdate-column",
    },
    {
      header: t("internManagement.columns.endDate"),
      accessor: (row) => formatDateShort(row.endDate),
      className: "intern-enddate-column",
    },
    {
      header: t("internManagement.columns.status"),
      accessor: (row) => {
        const config = STATUS_CONFIG[row.status];
        return (
          <Chip type={config.type} variant="outlined" size="small">
            {config.label}
          </Chip>
        );
      },
      className: "intern-status-column",
    },
    {
      header: t("internManagement.columns.school"),
      accessor: (row) => row.school ?? "—",
      className: "intern-school-column",
    },
    {
      header: t("internManagement.columns.schoolDepartment"),
      accessor: (row) => row.schoolDepartment ?? "—",
      className: "intern-dept-column",
    },
  ];

  const renderActions = (row: Intern) => {
    const rowId = row.id;
    const isOpen = openDropdowns[rowId] ?? false;
    const isCompleted = row.status === 1;
    const certificateReady = row.hasCertificate === true;
    const certificateProcessing =
      isCompleted && row.hasCertificate === false;
    const isCurrentCertificateLoading = certificateDownloadingId === row.id;
    const menuItems = [
      {
        label: t("internManagement.actions.internDetail"),
        icon: <EyeIcon width={20} height={20} />,
        onClick: () => {
          setSelectedIntern(row);
          setIsDetailDrawerOpen(true);
          handleDropdownToggle(rowId, false);
        },
      },
      canUpdate && {
        label: t("internManagement.actions.edit"),
        icon: <EditIcon width={20} height={20} />,
        onClick: () => {
          setEditInternId(row.id);
          setIsAddModalOpen(true);
          handleDropdownToggle(rowId, false);
        },
      },
      isCompleted &&
        certificateReady && {
          label: isCurrentCertificateLoading
            ? t("common.loading")
            : t("internManagement.certificate.download"),
          icon: <PdfIcon width={20} height={20} />,
          onClick: () => {
            if (isCurrentCertificateLoading) return;
            handleDropdownToggle(rowId, false);
            void handleDownloadCertificate(row);
          },
        },
      certificateProcessing && {
        label: t("internManagement.certificate.processing"),
        icon: <PdfIcon width={20} height={20} />,
        onClick: () => {
          handleDropdownToggle(rowId, false);
        },
      },
      canDelete && {
        label: t("internManagement.actions.delete"),
        icon: <DeleteIcon width={20} height={20} />,
        onClick: () => {
          setInternToDelete(row);
          handleDropdownToggle(rowId, false);
        },
      },
    ].filter(Boolean) as {
      label: string;
      icon: React.ReactNode;
      onClick: () => void;
    }[];
    const menuContent = (
      <div className={styles.actionMenu}>
        {menuItems.map((item, index) => (
          <button
            key={index}
            type="button"
            className={styles.actionMenuItem}
            onClick={(e) => {
              e.stopPropagation();
              item.onClick();
            }}
          >
            {item.icon}
            {item.label}
          </button>
        ))}
      </div>
    );
    return (
      <Popover
        content={menuContent}
        isOpen={isOpen}
        onClose={() => handleDropdownToggle(rowId, false)}
        position="bottom"
      >
        <button
          className={styles.actionButton}
          onClick={(e) => {
            e.stopPropagation();
            handleDropdownToggle(rowId, !isOpen);
          }}
        >
          <ActionsIcon width={20} height={20} />
        </button>
      </Popover>
    );
  };

  return (
    <DashboardLayout>
      <Header title={t("internManagement.title")} />

      <PageHeader
        searchPlaceholder={t("internManagement.searchPlaceholder")}
        searchValue={searchInput}
        onSearch={handleSearch}
        showSort
        sortOptions={sortOptions}
        selectedSortValue={`${sortBy}-${sortOrder}`}
        onSort={handleSortChange}
        filters={filterFields}
        onFilterChange={handleFilterChange}
        onAddNew={canCreate ? handleAddIntern : undefined}
        addButtonLabel={
          canCreate
            ? t("internManagement.addButton")
            : undefined
        }
        leftExtraContent={
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleExportExcel}
            disabled={isExporting}
            loading={isExporting}
            startIcon={<DownloadIcon width={18} height={18} />}
          >
            {t("internManagement.export.excel")}
          </Button>
        }
      />

      <Table
        columns={columns}
        data={interns}
        isLoading={isLoading}
        selectedRows={selectedRows}
        onRowSelect={handleRowSelect}
        onSelectAll={handleSelectAll}
        getRowId={(row) => row.id}
        currentPage={currentPage}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        renderActions={renderActions}
        openDropdowns={openDropdowns}
        emptyMessage={t("internManagement.noData")}
      />

      <AddInternModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditInternId(null);
        }}
        onSuccess={handleAddInternSuccess}
        editId={editInternId}
      />

      <InternDetailDrawer
        isOpen={isDetailDrawerOpen}
        onClose={() => {
          setIsDetailDrawerOpen(false);
          setSelectedIntern(null);
        }}
        intern={selectedIntern}
        onGoToDetail={(i) =>
          navigate(`/dashboard/staff-management/${i.userId ?? i.id}`)
        }
        onEdit={
          canUpdate
            ? (i) => {
                setEditInternId(i.id);
                setIsDetailDrawerOpen(false);
                setSelectedIntern(null);
                setIsAddModalOpen(true);
              }
            : undefined
        }
        onDelete={
          canDelete
            ? (i) => {
                setInternToDelete(i);
                setIsDetailDrawerOpen(false);
                setSelectedIntern(null);
              }
            : undefined
        }
      />

      <ConfirmModal
        isOpen={!!internToDelete}
        onClose={() => setInternToDelete(null)}
        onConfirm={handleConfirmDelete}
        title={t("internManagement.deleteConfirm.title")}
        message={
          internToDelete ? (
            <>
              {t("internManagement.deleteConfirm.message")}{" "}
              <strong>{internToDelete.name}</strong>
            </>
          ) : (
            ""
          )
        }
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        loading={deleteConfirmLoading}
        confirmVariant="danger"
      />
    </DashboardLayout>
  );
};

export default InternManagementPage;
